import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box, Paper, Backdrop, CircularProgress, Zoom, Fade, Typography
} from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import WowCarrello from "../../assets/login_watermark.svg";
import MajorBitLogo from "../../assets/majorbitgroup_black.svg";

/**
 * AuthLayout - Shared layout for Login and Password Recovery
 */
const AuthLayout = ({ 
  children, 
  title, 
  showOverlay, 
  success, 
  error, 
  loadingMessage, 
  successTitle, 
  successMessage, 
  errorTitle, 
  errorMessage,
  renderCustomErrorActions
}) => {
  const containerRef = useRef(null);
  const [diagonalAngle, setDiagonalAngle] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const h = containerRef.current.offsetHeight;
        const w = containerRef.current.offsetWidth;
        const screenAngle = Math.atan2(-h, w) * (180 / Math.PI);
        setDiagonalAngle(screenAngle);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderOverlayContent = () => {
    if (!success && !error) {
      return (
        <>
          <CircularProgress color="inherit" size={60} thickness={4} />
          <Fade in={true} timeout={800}>
            <Typography variant="h5" sx={{ fontWeight: 500, letterSpacing: 1, textAlign: 'center', px: 2 }}>
              {loadingMessage || "Elaborazione in corso..."}
            </Typography>
          </Fade>
        </>
      );
    }

    if (success) {
      return (
        <Zoom in={success} timeout={500}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center', px: 2 }}>
            <CheckCircleOutline sx={{ fontSize: 100, color: '#4caf50' }} />
            <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: 1 }}>
              {successTitle || "Operazione riuscita!"}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              {successMessage}
            </Typography>
          </Box>
        </Zoom>
      );
    }

    return (
      <Zoom in={error} timeout={500}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center', px: 2 }}>
          <ErrorOutline sx={{ fontSize: 100, color: '#f44336' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: 1 }}>
            {errorTitle || "Operazione Fallita"}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            {errorMessage}
          </Typography>
          {renderCustomErrorActions?.()}
        </Box>
      </Zoom>
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: "100vh",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(to bottom right, #0b3d87 0%, #1e62c9 50%, #f4f6f8 50%, #f4f6f8 100%)",
        p: { xs: 2, sm: 3 },
        boxSizing: "border-box",
      }}
    >
      <title>{title}</title>

      {/* Watermark SVG WOW */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: { xs: "120vw", md: "120vw", lg: "110vw" },
          height: "auto",
          transform: `translate(-50%, -50%) rotate(${diagonalAngle}deg) translateY(-50%)`,
          transformOrigin: "center center",
          opacity: 0.4,
          zIndex: 1,
          pointerEvents: "none",
          userSelect: "none",
          display: "flex",
          justifyContent: "center"
        }}
      >
        <img
          src={WowCarrello}
          alt="Watermark"
          style={{ width: '100%', height: 'auto', filter: "brightness(1.1)" }}
        />
      </Box>

      <Paper
        elevation={6}
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          width: "100%",
          maxWidth: { xs: "90%", sm: "350px" },
          borderRadius: 3,
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          zIndex: 2,
          position: "relative"
        }}
      >
        {children}
      </Paper>

      {/* Logo Major Bit Group */}
      <Box sx={{ position: "absolute", bottom: { xs: 16, sm: 24 }, right: { xs: 24, sm: 32 }, zIndex: 1 }}>
        <img
          src={MajorBitLogo}
          alt="Major Bit Group"
          style={{ height: "50px", objectFit: "contain", opacity: 0.8 }}
        />
      </Box>

      {/* Overlay Premium */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 5,
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(11, 61, 135, 0.6)",
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
        open={showOverlay}
      >
        {renderOverlayContent()}
      </Backdrop>
    </Box>
  );
};

AuthLayout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  showOverlay: PropTypes.bool.isRequired,
  success: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  loadingMessage: PropTypes.string,
  successTitle: PropTypes.string,
  successMessage: PropTypes.string,
  errorTitle: PropTypes.string,
  errorMessage: PropTypes.string,
  renderCustomErrorActions: PropTypes.func
};

export default AuthLayout;
 